import { useLocation, useParams, useNavigate } from "react-router-dom";
import React, { useState, useEffect, ChangeEvent } from "react";
import AdminHeader from "../../Components/AdminHeader/AdminHeader";
import AdminNav from "../../Components/AdminNav/AdminNav";
import { getGalleryById, editGallery } from "../../actions/Galleries";
import { IMAGE_URL } from "../../path";

interface GalleryImage {
    id?: number;
    gallery_id?: number;
    image_path?: string;
    preview?: string;
    display_order?: number;
    created_at?: string;
    updated_at?: string;
    deleted_at?: string | null;
    file?: File;
}

interface FormDataType {
    title: string;
    year_posted: string;
    description: string;
    cover_image?: File | null;
    images: GalleryImage[];
    existing_images: GalleryImage[];
}

function EditGallery() {
    const location = useLocation();
    const user = location.state?.user;
    const [isCollapsed, setIsCollapsed] = useState(true);
    const { id } = useParams<{ id?: string }>();
    const navigate = useNavigate();

    const [formData, setFormData] = useState<FormDataType>({
        title: '',
        year_posted: '',
        description: '',
        cover_image: null,
        images: [],
        existing_images: []
    });

    const [coverPreview, setCoverPreview] = useState<string>('');
    const [imagePreviews, setImagePreviews] = useState<string[]>([]);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (id) {
            getGalleryById(parseInt(id))
                .then(response => {
                    const gallery = response.data.gallery;
                    setFormData({
                        title: gallery.title,
                        year_posted: gallery.date_posted,
                        description: gallery.description || '',
                        cover_image: null,
                        images: [],
                        existing_images: gallery.images || []
                    });
                    setCoverPreview(gallery.cover_image ? IMAGE_URL + gallery.cover_image : '');
                    setImagePreviews(
                        (gallery.images || []).map((img: GalleryImage) => IMAGE_URL + img.image_path)
                    );
                })
                .catch(e => {
                    console.error("Failed to fetch gallery:", e);
                    setErrorMessage("Failed to load gallery data");
                });
        }
    }, [id]);

    const handleInputChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleCoverImageUpload = (e: ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            setFormData(prev => ({ ...prev, cover_image: file }));

            if (coverPreview && !coverPreview.startsWith(IMAGE_URL)) {
                URL.revokeObjectURL(coverPreview);
            }
            setCoverPreview(URL.createObjectURL(file));
        }
    };

    const handleGalleryImageUpload = (e: ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            const files = Array.from(e.target.files);
            const newImages: GalleryImage[] = files.map(file => ({
                file,
                preview: URL.createObjectURL(file)
            }));

            setFormData(prev => ({
                ...prev,
                images: [...prev.images, ...newImages]
            }));

            setImagePreviews(prev => [...prev, ...newImages.map(img => img.preview!)]);
        }
    };

    const removeGalleryImage = (index: number, isExisting: boolean) => {
        if (isExisting) {
            setFormData(prev => ({
                ...prev,
                existing_images: prev.existing_images.filter((_, i) => i !== index)
            }));
        } else {
            const adjustedIndex = index - formData.existing_images.length;
            const removed = formData.images[adjustedIndex];
            if (removed.preview) URL.revokeObjectURL(removed.preview);

            setFormData(prev => ({
                ...prev,
                images: prev.images.filter((_, i) => i !== adjustedIndex)
            }));
        }
        setImagePreviews(prev => prev.filter((_, i) => i !== index));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setErrorMessage(null);
        setIsSubmitting(true);

        try {
            const formToSend = new FormData();
            formToSend.append('title', formData.title);
            formToSend.append('year_posted', formData.year_posted);
            formToSend.append('description', formData.description);

            if (formData.cover_image) {
                formToSend.append('cover_image', formData.cover_image);
            }

            // Append new images
            formData.images.forEach((img, index) => {
                if (img.file) formToSend.append(`images[${index}]`, img.file);
            });

            // Append existing images as array of objects (id required)
            formToSend.append('existing_images', JSON.stringify(
                formData.existing_images.map(img => ({ id: img.id }))
            ));

            if (id) {
                const response = await editGallery(formToSend, parseInt(id));
                if (response.status === 201 || response.status === 200) {
                    navigate('/dashboard/galleries/list', {
                        state: {
                            user,
                            successMessage: 'Gallery updated successfully'
                        }
                    });
                }
            }
        } catch (error: any) {
            setErrorMessage(error.response?.data?.message || 'Error updating gallery');
            console.error('Error updating gallery:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div>
            <AdminHeader isCollapsed={isCollapsed} setIsCollapsed={setIsCollapsed} user={user} />
            <AdminNav collapsed={isCollapsed} setCollapsed={setIsCollapsed} user={user} />
            <div className={`main-admin-page ${isCollapsed ? 'collapsed' : 'expanded'}`}>
                <div style={{ padding: '20px' }}>
                    <h2>Edit Gallery</h2>
                    {errorMessage && <div style={{ padding: '10px', marginBottom: '20px', backgroundColor: '#ffebee', color: '#c62828', borderRadius: '4px' }}>{errorMessage}</div>}

                    <form onSubmit={handleSubmit}>
                        <div style={{ marginBottom: '1rem' }}>
                            <input type="text" name="title" value={formData.title} onChange={handleInputChange} placeholder="Title" style={{ width: '100%', padding: '8px' }} required />
                        </div>

                        <div style={{ marginBottom: '1rem' }}>
                            <input type="date" name="year_posted" value={formData.year_posted} onChange={handleInputChange} style={{ width: '100%', padding: '8px' }} required />
                        </div>

                        <div style={{ marginBottom: '1rem' }}>
                            <textarea name="description" value={formData.description} onChange={handleInputChange} placeholder="Description (optional)" style={{ width: '100%', padding: '8px', minHeight: '100px', resize: 'vertical' }} />
                        </div>

                        <div style={{ marginBottom: '1rem' }}>
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Cover Image</label>
                            <input type="file" accept="image/*" onChange={handleCoverImageUpload} />
                            {coverPreview && <div style={{ maxWidth: '300px', border: '1px solid #ddd', borderRadius: '4px', overflow: 'hidden', margin: '10px auto' }}>
                                <img src={coverPreview} alt="Cover preview" style={{ width: '100%', height: 'auto', display: 'block' }} />
                            </div>}
                        </div>

                        <div style={{ marginBottom: '1rem', display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
                            <label style={{ marginBottom: '8px', fontWeight: 'bold' }}>Add New Gallery Images</label>
                            <input type="file" multiple accept="image/*" onChange={handleGalleryImageUpload} style={{ maxWidth: '500px', padding: '8px' }} />
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '1rem', padding: '1rem 0', maxWidth: '2000px', margin: '0 auto' }}>
                            {[...formData.existing_images, ...formData.images].map((img, index) => {
                                const src = img.image_path ? IMAGE_URL + img.image_path : img.preview!;
                                const isExisting = !!img.image_path;
                                return (
                                    <div key={index} style={{ position: 'relative', aspectRatio: '1', border: '1px solid #ddd', borderRadius: '4px', overflow: 'hidden' }}>
                                        <img src={src} alt={`Preview ${index + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                        <button type="button" onClick={() => removeGalleryImage(index, isExisting)} style={{ position: 'absolute', top: '5px', right: '5px', background: 'rgba(255,255,255,0.8)', border: 'none', borderRadius: '50%', width: '24px', height: '24px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ff4444', fontWeight: 'bold' }}>✕</button>
                                    </div>
                                );
                            })}
                        </div>

                        <div style={{ marginTop: '2rem', display: 'flex', justifyContent: 'center', width: '100%' }}>
                            <button type="submit" className="btn btn-info" style={{ width: '10%', display: 'flex', textAlign: "center", alignItems: "center", justifyContent: "center" }} disabled={isSubmitting}>
                                {isSubmitting ? 'Updating Gallery...' : 'Update Gallery'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}

export default EditGallery;
